<template>
  <div class="fixed inset-0 z-[9999] flex flex-col bg-gray-900">
    <!-- Header -->
    <div
      class="flex-shrink-0 border-b border-gray-700 px-6 pt-[max(1rem,env(safe-area-inset-top))] pb-4"
    >
      <h1 class="font-fredoka text-2xl text-white">Privacy Policy</h1>
      <p class="font-fredoka text-sm text-gray-400">
        Version {{ version }} · Please read and scroll to the bottom to agree
      </p>
    </div>

    <!-- Scrollable policy content -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto px-6 py-4" @scroll="onScroll">
      <div class="font-fredoka space-y-5 text-sm leading-relaxed text-gray-300">
        <section>
          <h2 class="mb-1 text-base text-white">1. Data Controller</h2>
          <p>
            Spellfusion GmbH is responsible for the processing of your personal data within the
            Retryli app ("we", "us", "our").
          </p>
          <p class="mt-1">Contact: <span class="text-indigo-400">support@spellfusion.com</span></p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">2. Data We Collect</h2>
          <p>We collect and process the following personal data:</p>
          <ul class="mt-2 list-inside list-disc space-y-1 pl-2">
            <li>
              <strong class="text-white">Account data:</strong> Email address and a unique user ID
              provided by Firebase Authentication (Google LLC) when you sign in.
            </li>
            <li>
              <strong class="text-white">App content:</strong> Data you enter in the app, including
              activity categories, tasks, mood entries, gratitude notes, and headache logs. This
              data is stored in your personal Firebase Firestore database.
            </li>
            <li>
              <strong class="text-white">Usage analytics:</strong> Anonymised usage data (screens
              visited, feature interactions) collected via Firebase Analytics (Google LLC) to help
              us improve the app.
            </li>
            <li>
              <strong class="text-white">Crash reports:</strong> Technical error data (device type,
              OS version, stack traces) collected via Sentry (Functional Software Inc.) when the app
              crashes or encounters an error.
            </li>
          </ul>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">3. Legal Basis for Processing</h2>
          <p>We process your data on the following legal bases under GDPR Art. 6:</p>
          <ul class="mt-2 list-inside list-disc space-y-1 pl-2">
            <li>
              <strong class="text-white">Contract (Art. 6(1)(b)):</strong> Account data and app
              content are necessary to provide the service you signed up for.
            </li>
            <li>
              <strong class="text-white">Consent (Art. 6(1)(a)):</strong> Analytics and crash
              reporting. You agree to these by accepting this policy. You may withdraw consent at
              any time by contacting us.
            </li>
            <li>
              <strong class="text-white">Legitimate interest (Art. 6(1)(f)):</strong> Crash reports
              help us maintain app stability and security.
            </li>
          </ul>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">4. Data Retention</h2>
          <p>
            Your account data and app content are retained for as long as your account is active.
            Crash reports are retained for 90 days by Sentry. Analytics data is retained for 14
            months by Google. You may request deletion of your data at any time (see Section 7).
          </p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">5. Third-Party Services</h2>
          <p>
            We use the following sub-processors, each with their own GDPR-compliant data processing
            agreements:
          </p>
          <ul class="mt-2 list-inside list-disc space-y-1 pl-2">
            <li>
              <strong class="text-white">Google Firebase</strong> (Google LLC, USA) —
              Authentication, database, and analytics. Google participates in the EU-U.S. Data
              Privacy Framework.
            </li>
            <li>
              <strong class="text-white">Sentry</strong> (Functional Software Inc., USA) — Crash
              reporting. Sentry participates in the EU-U.S. Data Privacy Framework.
            </li>
          </ul>
          <p class="mt-2">
            Your data may be transferred to and processed in the United States under the appropriate
            safeguards described above.
          </p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">6. Data Security</h2>
          <p>
            All data is encrypted in transit (TLS) and at rest. Access to your Firestore data is
            protected by Firebase security rules tied to your authenticated user ID.
          </p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">7. Your Rights</h2>
          <p>Under GDPR, you have the following rights regarding your personal data:</p>
          <ul class="mt-2 list-inside list-disc space-y-1 pl-2">
            <li>
              <strong class="text-white">Access (Art. 15):</strong> Request a copy of your data. Use
              the "Export Data" feature in Settings for a full local export.
            </li>
            <li>
              <strong class="text-white">Rectification (Art. 16):</strong> Correct inaccurate data
              directly in the app.
            </li>
            <li>
              <strong class="text-white">Erasure (Art. 17):</strong> Request deletion of your
              account and all associated data.
            </li>
            <li>
              <strong class="text-white">Portability (Art. 20):</strong> Export your data in
              machine-readable JSON format via Settings → Export Data.
            </li>
            <li>
              <strong class="text-white">Objection (Art. 21):</strong> Object to processing based on
              legitimate interest.
            </li>
            <li>
              <strong class="text-white">Withdraw consent:</strong> Withdraw consent for analytics
              and crash reporting at any time.
            </li>
          </ul>
          <p class="mt-2">
            To exercise any of these rights, contact us at
            <span class="text-indigo-400">support@spellfusion.com</span>. We will respond within 30
            days.
          </p>
          <p class="mt-2">
            You also have the right to lodge a complaint with your local data protection authority.
          </p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">8. Changes to This Policy</h2>
          <p>
            If we make material changes to this policy, you will be asked to review and agree to the
            updated version before continuing to use the app.
          </p>
        </section>

        <section>
          <h2 class="mb-1 text-base text-white">9. Contact</h2>
          <p>
            For any privacy-related questions or requests:
            <span class="text-indigo-400">support@spellfusion.com</span>
          </p>
        </section>

        <!-- Spacer so the last section isn't flush against the button -->
        <div class="h-4" />
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex-shrink-0 border-t border-gray-700 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <p v-if="!scrolledToBottom" class="font-fredoka mb-3 text-center text-xs text-gray-500">
        Scroll to the bottom to enable the agree button
      </p>
      <button
        :disabled="!scrolledToBottom"
        class="font-fredoka w-full rounded-xl py-3 text-lg text-white transition-all duration-200"
        :class="
          scrolledToBottom
            ? 'bg-indigo-500 hover:bg-indigo-400 active:bg-indigo-600'
            : 'cursor-not-allowed bg-gray-700 text-gray-500'
        "
        @click="agree"
      >
        I have read and agree to the Privacy Policy
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { CURRENT_POLICY_VERSION } from '../services/PolicyService.ts';

const emit = defineEmits<{ agreed: [] }>();

const version = CURRENT_POLICY_VERSION;
const scrolledToBottom = ref(false);
const scrollContainer = ref<HTMLElement | null>(null);

function onScroll() {
  const el = scrollContainer.value;
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
    scrolledToBottom.value = true;
  }
}

function agree() {
  if (scrolledToBottom.value) emit('agreed');
}
</script>
